import Litepicker from "litepicker";
import 'litepicker/dist/plugins/multiselect';
import 'litepicker/dist/plugins/mobilefriendly';
import { ILPConfiguration } from "litepicker/dist/types/interfaces";
import { DateTime } from "litepicker/dist/types/datetime";

enum CInputDatePickerMode {
    single = "single",
    range = "range",
    multiselect = "multiselect",
}

type TDatePickerMode = keyof typeof CInputDatePickerMode & string;

interface IInputDatePicker {
    Element: HTMLElement | HTMLInputElement;
    NumberOfMonths?: number;
    NumberOfColumns?: number;
    Lang?: string;
    ButtonsText?: Partial<ILPConfiguration["buttonText"]>;
    TooltipText?: Partial<ILPConfiguration["tooltipText"]>;
    Position?: string;
}

interface IInputDatePickerSingle {
    format?: string | object;
}

interface IInputDatePickerRange {
    format?: string | object;
    minDays?: number;
    maxDays?: number;
    disallowLockDaysInRange?: boolean;
    lockDaysInclusivity?: string;
}

interface IInputDatePickerMultiselect {
    multiselect: ILPConfiguration["multiselect"];
}

export type IInputDatePickerConfig = IInputDatePicker & {
    [F in TDatePickerMode]: {
        Mode: F;
        OnSelect?: (data: number[]) => void;
        SingleDatePickerAttrs?: F extends "single" ? IInputDatePickerSingle : never;
        RangeDatePickerAttrs?: F extends "range" ? IInputDatePickerRange : never;
        MultiselectDatePickerAttrs?: F extends "multiselect" ? IInputDatePickerMultiselect : never;
    }
}[TDatePickerMode]

export class InputDatePicker {
    private litePickerConfig: ILPConfiguration;
    private mode: TDatePickerMode
    private litePicker: Litepicker;
    private onSelect: (data: number[]) => void;
    private observer: MutationObserver | null = null;
    private observedElement: HTMLElement | HTMLInputElement;

    constructor(config: IInputDatePickerConfig) {
        this.mode = config.Mode;
        this.observedElement = config.Element;
        this.litePickerConfig = <ILPConfiguration>{
            element: config.Element,
            position: config.Position ?? "bottom",
            numberOfMonths: config.NumberOfMonths,
            numberOfColumns: config.NumberOfColumns,
            lang: config.Lang,
            buttonText: config.ButtonsText,
            tooltipText: config.TooltipText,
            ...config.SingleDatePickerAttrs,
            ...config.RangeDatePickerAttrs,
            ...config.MultiselectDatePickerAttrs,
        }
        this.onSelect = config.OnSelect;
        this.Init();
    }

    private Init() {
        const cf = this.litePickerConfig;
        let plugins: string[] = [];
        plugins.push("mobilefriendly");

        if (this.mode == "multiselect")
            plugins.push('multiselect');

        this.litePicker = new Litepicker({
            ...cf,
            plugins,
            autoRefresh: true,
            singleMode: this.mode == "single",
            mobileFriendly: true,
            setup: (picker) => {
                if (this.mode !== "multiselect") {
                    picker.on('selected', (dt1: (DateTime | null), dt2: (DateTime | null)) => {
                        if (this.onSelect) {
                            this.onSelect([dt1, dt2].flatMap((val) => val?.getTime() ?? []));
                            return
                        }
                    })
                }

                picker.on('button:apply', () => {
                    let datesSelection = ((picker as any).multipleDates as number[]);
                    if (this.onSelect) {
                        this.onSelect(datesSelection);
                        return
                    }
                })
            },
        });

        this.setUpDisconnectedObserver();
    }

    private setUpDisconnectedObserver() {
        this.observer = new MutationObserver(() => {
            if (!document.body.contains(this.observedElement)) {
                this.destroy();
            }
        })

        this.observer.observe(document.body, { attributes: true, childList: true, subtree: true });
    }

    private getSelectedDateTimes() {
        let date: number[];
        switch (this.mode) {
            case "single":
                date = [this.litePicker.getDate()?.getTime()].filter(d => d);
                break;
            case "range":
                date = [this.litePicker.getStartDate()?.getTime(), this.litePicker.getEndDate()?.getTime()].filter(d => d);
                break;
            case "multiselect":
                date = ((this.litePicker as any).multipleDates as number[])
                break;
        }
        return date
    }

    public getTimeNumberValue(): number[] {
        return this.getSelectedDateTimes();
    }

    public getDateValue(): Date[] {
        return this.getTimeNumberValue().map(d => new Date(d));
    }

    public getDateISOValue(): string[] {
        return this.getDateValue().map(d => d.toISOString());
    }

    public setDate(dates: Date[]) {
        if (!dates.length) {
            return
        }
        let dates2 = [...dates];
        dates2 = dates2.map(d => {
            d.setHours(0)
            d.setMinutes(0)
            d.setSeconds(0)
            d.setMilliseconds(0)
            return d
        })
        switch (this.mode) {
            case "single":
                let date = dates[0];
                this.litePicker.setDate(date.getTime());
                break;
            case "range":
                let date1 = dates[0];
                let date2 = dates[1];
                this.litePicker.setDateRange(date1.getTime(), date2.getTime())
                break;
            case "multiselect":
                ((this.litePicker as any).multipleDates as number[]) = dates2.map(d => d.getTime());
                break;
        }

        if (this.onSelect) {
            this.onSelect(this.getSelectedDateTimes());
        }

        this.goToDate(dates2[0]);
    }

    public goToDate(date: Date) {
        if (!date) return;
        this.litePicker.gotoDate(date);
    }

    public destroy(): void {
        if (!this.litePicker) return;
        this.litePicker.destroy();
        this.litePicker = null;

        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
}